"use client"

import { useCallback, useRef, useState } from "react"
import Image, { type ImageProps } from "next/image"

import { cn } from "@/lib/utils"

interface ZoomableImageProps
  extends Omit<ImageProps, "onMouseMove" | "onMouseLeave" | "className"> {
  className?: string
  zoomScale?: number
}

export function ZoomableImage({
  className,
  zoomScale = 1.2,
  alt = "",
  ...props
}: ZoomableImageProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [transformOrigin, setTransformOrigin] = useState("center")
  const [isZoomed, setIsZoomed] = useState(false)

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return

    const rect = containerRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    // Calculate the percentage position of the mouse with bounds checking
    const xPercent = Math.max(0, Math.min(100, (x / rect.width) * 100))
    const yPercent = Math.max(0, Math.min(100, (y / rect.height) * 100))

    setTransformOrigin(`${xPercent}% ${yPercent}%`)
    setIsZoomed(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsZoomed(false)
    // Small delay to ensure smooth transition when moving out
    setTimeout(() => {
      setTransformOrigin("center")
    }, 100)
  }, [])

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative overflow-hidden transition-all duration-200 ease-out",
        className
      )}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        transformOrigin,
        transform: isZoomed ? `scale(${zoomScale})` : "none",
        transitionProperty: "transform, transform-origin",
      }}
    >
      <Image {...props} alt={alt} className="w-full h-full object-cover" />
    </div>
  )
}
